import React, { createContext, useContext, useMemo } from "react";
import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestConfig
} from "axios";
import { useAuth } from "./AuthProvider";
import { useTenant } from "./TenantProvider";
import { ApiResponse } from "../types";
import { TokenRefreshService } from "../services/TokenRefreshService";
import {
  isRefreshCredentialUnusable,
  isSessionDead
} from "../services/sessionStatus";
import { ADMIN_SESSION_KEY, readStoredTokens } from "../services/tokenStorage";
import type { RefreshTrigger } from "../services/TokenRefreshService";
import { trackEvent } from "../services/telemetry";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    // Set by the RESPONSE interceptor once it has refreshed and replayed this request. It is
    // the loop guard: a replay that 401s again is rejected rather than refreshed forever.
    _retry?: boolean;
    // Set by the REQUEST interceptor on a pre-emptive refresh. Separate from _retry so a request
    // that refreshed ahead of expiry and still 401s (its token rotated out by another tab) is not
    // read as already-retried and denied its one reactive refresh (AB#9773).
    _proactivelyRefreshed?: boolean;
    // Whether an admin refresh session existed when this request was first built. Carried on
    // the config because it has to outlive any refresh attempt made on the request's behalf.
    _hadAdminSession?: boolean;
  }
}

interface ApiContextValue {
  request<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>>;
  get<T>(url: string, params?: any): Promise<ApiResponse<T>>;
  post<T, D = any>(url: string, data: D): Promise<ApiResponse<T>>;
  put<T, D = any>(url: string, data: D): Promise<ApiResponse<T>>;
  patch<T, D = any>(url: string, data: D): Promise<ApiResponse<T>>;
  del<T>(url: string, params?: any): Promise<ApiResponse<T>>;
}

const ApiContext = createContext<ApiContextValue | null>(null);

// Refresh slightly ahead of the JWT's exp claim rather than at the exact instant it lapses.
// Without this, a request built a few ms before expiry sails through the check below with a
// token that is already dead by the time it reaches the server, buying nothing but an extra
// 401 round trip through the response interceptor.
const EXPIRY_SKEW_MS = 30000;

/**
 * What a refresh failure actually cost the user, which the refresh service itself cannot know.
 *
 * `ClientTokenRefresh` records that a refresh failed; this records what happened next, and the two
 * are not the same question. `auth-cleared` and `forced-login-redirect` are visible logouts.
 * `request-failed-transient` is the invisible one -- the refresh failed, the session was NOT
 * treated as dead, so nothing told the user anything and a widget just stopped working. That case
 * had no signal of any kind before this (AB#10739).
 *
 * One event with an outcome dimension rather than five separately named counters: it is the shape
 * the server side already uses, it keeps the sampling exemption to a single entry, and a dashboard
 * can pivot on it instead of unioning five tables.
 */
const CONSEQUENCE_EVENT = "ClientRefreshConsequence";

function trackConsequence(outcome: string, extra?: Record<string, string>): void {
  trackEvent(CONSEQUENCE_EVENT, { outcome, ...(extra || {}) }, undefined, true);
}

/**
 * The path only, never the query string. telemetry.ts strips it from `page` for a reason -- ids
 * and tokens live in query strings and this is telemetry, not a session recorder -- and a
 * per-request URL would be an unbounded dimension besides.
 */
function pathOf(url: string | undefined): string {
  if (!url) return "unknown";
  const cut = url.indexOf("?");
  const path = cut === -1 ? url : url.slice(0, cut);

  // Templated, not raw. The dashboard question is always "which endpoint", never "which row", so
  // the ids buy nothing and cost two things: they make the dimension unbounded, and a meeting or
  // project id is tenant-identifying in telemetry that outlives the request.
  return path
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/{guid}")
    .replace(/\/\d+/g, "/{id}");
}

let loginRedirectStarted = false;

function redirectToLogin(): void {
  if (loginRedirectStarted) return;
  if (window.self !== window.top) return;
  loginRedirectStarted = true;
  // The "I got kicked out" number. Counted here rather than at the call site so it cannot drift
  // from the redirect it describes, and after the guards above so a suppressed redirect -- already
  // started, or inside an iframe -- is not counted as one that happened.
  trackConsequence("forced-login-redirect");
  const returnUrl = encodeURIComponent(
    window.location.pathname + window.location.search + window.location.hash
  );
  window.location.assign(`/Account/Login?returnUrl=${returnUrl}`);
}

export const useApi = () => {
  const c = useContext(ApiContext);
  if (!c) throw new Error("useApi must be inside ApiProvider");
  return c;
};

interface ApiProviderProps {
  children: React.ReactNode;
  /**
   * Whether a dead session sends the user to the login page. Defaults to true. Separately gated on
   * `_hadAdminSession` (a stored refresh id), so a public or embedded page is never bounced to an
   * admin login by this default.
   */
  redirectOnSessionLoss?: boolean;
}

export const ApiProvider: React.FC<ApiProviderProps> = ({
  children,
  redirectOnSessionLoss = true
}) => {
  const {
    authData,
    setTokens,
    clearAuth,
    isEmbedSession,
    onSessionUnrecoverable
  } = useAuth();
  const { tenantId } = useTenant();

  const authRef = React.useRef(authData);
  React.useEffect(() => {
    authRef.current = authData;
  }, [authData]);

  // Stored values win over React state (a per-tab cache, possibly a rotation stale) — but only for
  // a real admin session. An embed shares localStorage with any admin in the same browser, so
  // reading storage would let it rotate the admin's family out; an embed reads only its own
  // in-memory value (FIX-8).
  // An embed falls back to "" rather than null, because for an embed those two are not the same
  // statement. "" means "asked and answered — this session can never refresh" and short-circuits
  // TokenRefreshService without a round trip; null means "nothing stored yet, worth asking". The
  // fact is a property of the session, not of whatever authData currently holds, so it is derived
  // from isEmbedSession here rather than read off a field that can go away — toAuthData now
  // returns null for a token that will not decode, which would otherwise silently turn "never
  // refreshes" into "please refresh me" on the surface the sentinel was written for (AB#10507).
  // The non-embed chain uses `||`, not `??`, for the same reason: outside an embed an empty
  // string is not a statement, it is a missing value, and `??` would pass it through as the
  // sentinel and refuse to refresh a perfectly live admin session.
  const getRefreshTokenId = React.useCallback(
    () =>
      isEmbedSession
        ? (authRef.current?.refreshTokenId ?? "")
        : (readStoredTokens().refreshTokenId ||
          authRef.current?.refreshTokenId ||
          null),
    [isEmbedSession]
  );

  // TokenRefreshService clears auth on a session-dead failure — right for an admin tab, wrong for
  // an embed that can re-exchange (a public dashboard has no refresh token, so the first attempt
  // wiped the token and every later request went anonymous, AB#9773).
  const canRecoverSession = Boolean(isEmbedSession && onSessionUnrecoverable);

  const clearAuthUnlessRecoverable = React.useCallback(() => {
    if (canRecoverSession) return;
    clearAuth();
  }, [canRecoverSession, clearAuth]);

  // Held in a ref, created once, and deliberately NOT computed inside the callbacks memo below.
  // That memo's identity changes whenever setTokens / clearAuth / getRefreshTokenId change, and a
  // waiter that subscribed under the old key would never be resolved OR rejected by a flight now
  // keyed differently - a permanently pending promise inside an axios interceptor, which surfaces
  // as a request that hangs with no error at all.
  //
  // An admin provider shares one key with every other admin caller, because they are all rotating
  // the same browser-wide family and single-flighting them is the point. An embed holds an exchanged
  // token that is nobody else's business, so it gets a key unique to this instance and can neither
  // join nor be joined to anyone else's flight (AB#10738).
  const sessionKeyRef = React.useRef<string>(
    isEmbedSession
      ? `embed-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
      : ADMIN_SESSION_KEY
  );

  const callbacks = useMemo(
    () =>
      Object.freeze({
        sessionKey: sessionKeyRef.current,
        setTokens,
        clearAuth: clearAuthUnlessRecoverable,
        getRefreshTokenId
      }),
    [setTokens, clearAuthUnlessRecoverable, getRefreshTokenId]
  );

  const refreshToken = React.useCallback(
    (presentedToken?: string, trigger: RefreshTrigger = "unknown"): Promise<string> =>
      TokenRefreshService.refreshToken(callbacks, presentedToken, trigger),
    [callbacks]
  );

  const client = useMemo<AxiosInstance>(() => {
    const c = axios.create({
      baseURL: window._JSCONFIG.ReactAppApiCoreBaseUrl
    });

    c.interceptors.request.use(async (cfg) => {
      cfg.headers = cfg.headers || {};

      // Recorded before anything on this request can clear auth -- including the proactive
      // refresh below, which wipes storage and the cookie on a session-dead failure. This is
      // the only point at which the answer is still trustworthy. Guarded so the retry pass
      // (which re-enters this interceptor) cannot overwrite the original capture.
      if (cfg._hadAdminSession === undefined) {
        cfg._hadAdminSession = Boolean(getRefreshTokenId());
      }

      if (tenantId) {
        cfg.headers["X-Tenant-Id"] = tenantId;
      }

      if (cfg.headers.Authorization) {
        return cfg;
      }

      const currentAuth = authRef.current;
      if (!currentAuth?.accessToken) {
        // No usable token, but this browser still holds a refresh id — so a token is obtainable
        // and sending anonymously would spend a guaranteed 401 to discover that. This is the
        // state a browser lands in after a poisoned value is discarded, and the state of a tab
        // whose first request beats the seeding script. An embed is excluded for free:
        // getRefreshTokenId returns "" there, which is falsy here and terminal in rotate().
        if (!cfg._retry && !cfg._proactivelyRefreshed && getRefreshTokenId()) {
          try {
            const bootstrapped = await refreshToken(undefined, "bootstrap");
            cfg._proactivelyRefreshed = true;
            cfg.headers.Authorization = `Bearer ${bootstrapped}`;
          } catch (_) {
            // Fall through anonymous; the response interceptor still owns the 401.
          }
        }

        return cfg;
      }

      const now = Date.now();
      if (
        currentAuth.expiryTime &&
        now >= currentAuth.expiryTime - EXPIRY_SKEW_MS &&
        !cfg._retry &&
        !cfg._proactivelyRefreshed
      ) {
        try {
          const newToken = await refreshToken(
            currentAuth.accessToken,
            "proactive-interceptor"
          );
          // Not _retry: this request has not been retried, it has been refreshed. Marking it
          // as retried would spend its one reactive refresh before the response is even back.
          cfg._proactivelyRefreshed = true;
          cfg.headers.Authorization = `Bearer ${newToken}`;
          return cfg;
        } catch (error) {
          // The proactive refresh failed (e.g. offline, or the request raced a refresh
          // already in flight from another request). Fall through and send the stale
          // token rather than nothing: an anonymous request throws away two things the
          // response interceptor's own refresh depends on -- isSessionDead() needs a real
          // 401 to distinguish a dead session from a network blip, and readAdoptableToken()
          // needs the presented token's principal to adopt a fresher token another tab may
          // have already rotated in. Sending no header forces an extra round trip to
          // /account/refresh even when a sibling tab's storage already has a live token.
          cfg.headers.Authorization = `Bearer ${currentAuth.accessToken}`;
          return cfg;
        }
      }

      cfg.headers.Authorization = `Bearer ${currentAuth.accessToken}`;

      return cfg;
    });

    c.interceptors.response.use(
      (res) => res,
      async (error: AxiosError) => {
        const origReq = error.config as InternalAxiosRequestConfig;

        // Refreshed, replayed, and refused again. Diagnostic of the problems a refresh cannot fix:
        // clock skew, stale claims, or an authorization failure being mistaken for an expired
        // token. It falls straight through the block below because _retry is already set, so it
        // has to be counted here or not at all.
        if (error.response?.status === 401 && origReq?._retry) {
          trackConsequence("retry-still-401", {
            url: pathOf(origReq.url)
          });
        }

        if (error.response?.status === 401 && origReq && !origReq._retry) {
          origReq._retry = true;

          // Read the value the request interceptor captured rather than re-reading storage.
          // By the time we get here auth may already be gone: a near-expiry request runs a
          // proactive refresh in the request interceptor, and that refresh calls clearAuth()
          // itself on a session-dead failure -- a whole round trip ago, so React state has
          // caught up too. Recomputing here would read false for exactly the signed-in admin
          // this redirect exists to rescue. Falls back to a live read only for a request that
          // never passed through our request interceptor.
          const hadAdminSession =
            origReq._hadAdminSession ?? Boolean(getRefreshTokenId());

          try {
            const presented = (
              origReq.headers?.Authorization as string | undefined
            )?.replace(/^Bearer /, "");
            const newToken = await refreshToken(presented, "reactive-401");
            origReq.headers = origReq.headers || {};
            origReq.headers.Authorization = `Bearer ${newToken}`;
            return c(origReq);
          } catch (refreshErr) {
            if (!isSessionDead(refreshErr)) {
              // The silent case. Auth is not cleared and no redirect fires, by design -- a network
              // blip must not log anyone out -- so the request simply fails and the user sees a
              // broken widget with no explanation. Nothing counted this before, which is why the
              // 400 dead end of AB#10737 could strand admins without showing up anywhere.
              trackConsequence("request-failed-transient", {
                url: pathOf(origReq.url),
                hadAdminSession: hadAdminSession ? "true" : "false"
              });
              return Promise.reject(refreshErr);
            }

            // An embed that can re-run its own exchange is not out of options. clearAuth here is
            // what left the public dashboard permanently anonymous (AB#9773). The handler re-seeds a
            // token; this request still fails, and the page's queries re-run against the new one.
            if (canRecoverSession && onSessionUnrecoverable) {
              trackConsequence("embed-recovery-attempted");
              try {
                await onSessionUnrecoverable();
              } catch (recoveryErr) {
                // The handler owns its own retry schedule. Losing this attempt must not
                // replace the caller's error with one about the recovery.
                console.error("Session recovery failed:", recoveryErr);
              }
              return Promise.reject(refreshErr);
            }

            // The user-visible logout count. Recorded before the redirect so it survives the
            // navigation that redirectToLogin is about to start.
            trackConsequence("auth-cleared", {
              hadAdminSession: hadAdminSession ? "true" : "false",
              willRedirect:
                redirectOnSessionLoss && hadAdminSession ? "true" : "false"
            });

            clearAuth();

            // OR, not a replacement. `hadAdminSession` reads stored tokens, which is exactly what a
            // client-side sign-out just deleted — so in the one state AB#10737 is about it is always
            // false, and the redirect that should rescue the admin never fires. The server's code
            // covers that hole; `hadAdminSession` still covers the cases the server cannot speak to,
            // notably a refresh that failed with no HTTP status at all (AB#10507's non-JWT 2xx).
            //
            // A public-page visitor is excluded because they present no refresh secret, so their 401
            // carries no code and their storage is empty — both halves false, no redirect (AB#9773).
            if (
              redirectOnSessionLoss &&
              (hadAdminSession || isRefreshCredentialUnusable(refreshErr))
            ) {
              redirectToLogin();
            }
            return Promise.reject(refreshErr);
          }
        }

        return Promise.reject(error);
      }
    );

    return c;
  }, [
    clearAuth,
    tenantId,
    refreshToken,
    getRefreshTokenId,
    redirectOnSessionLoss,
    canRecoverSession,
    onSessionUnrecoverable
  ]);

  const api = useMemo<ApiContextValue>(() => {
    async function request<T>(
      cfg: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
      try {
        const resp = await client.request<ApiResponse<T>>(cfg);
        if (resp.status === 204 || resp.data == null || resp.data === "") {
          return {
            success: true,
            message: "",
            data: undefined as unknown as T
          };
        }
        return resp.data;
      } catch (error) {
        const axiosError = error as AxiosError<ApiResponse<T>>;

        if (axiosError.response?.data) {
          return axiosError.response.data;
        }

        return {
          success: false,
          message: axiosError.message || "Request failed",
          data: {} as T,
          errors: {
            code: axiosError.code || "NETWORK_ERROR",
            description:
              axiosError.message || "An error occurred while making the request"
          }
        };
      }
    }

    function get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
      return request({ url, method: "GET", params });
    }

    function post<T, D = any>(url: string, data: D): Promise<ApiResponse<T>> {
      return request({ url, method: "POST", data });
    }

    function put<T, D = any>(url: string, data: D): Promise<ApiResponse<T>> {
      return request({ url, method: "PUT", data });
    }

    function patch<T, D = any>(url: string, data: D): Promise<ApiResponse<T>> {
      return request({ url, method: "PATCH", data });
    }

    function del<T>(url: string, params?: any): Promise<ApiResponse<T>> {
      return request({ url, method: "DELETE", params });
    }

    return { request, get, post, put, patch, del };
  }, [client]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};
